import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import CalloutBox from '../callout-box';
import { QuestionBox, StartAgain } from './styles';
import Options from './Options';
import StepTitle from './StepTitle';
import { VisuallyHidden } from '../styled/accessibility';
import { RichTextPropTypes } from '../../prop-types';

function traverseTree(selected, step, allSteps) {
  if (selected.length < 1 || !step.options) return [];

  const selectedOption = step.options.find(
    (option) => option.id === selected[0]
  );

  if (!selectedOption) {
    return [];
  }

  const nextStep = allSteps.find((item) => item.id === selectedOption.step.id);
  return [nextStep, ...traverseTree(selected.slice(1), nextStep, allSteps)];
}

const Stepper = ({ title, bodyText, options, allSteps }) => {
  const [selectedContentfulSteps, setSelectedContentfulSteps] = useState([]);
  const [liveTitle, setLiveTitle] = useState('');
  const [selectedOptionText, setSelectedOptionText] = useState('');

  const rootStep = {
    title,
    bodyText,
    options,
  };

  const currentRef = useRef(null);

  return (
    <form>
      {[
        rootStep,
        ...traverseTree(selectedContentfulSteps, rootStep, allSteps),
      ].map((step, i, steps) =>
        step.options === null ? (
          <CalloutBox
            heading={step.title}
            key={`qb_${i}_${step.id}`}
            content={step.bodyText}
          />
        ) : (
          <QuestionBox
            key={`qb_${i}_${step.id}`}
            ref={i === steps.length - 1 ? currentRef : null}
            $isAnAnswer={step.options === null}
            data-testid="question-box"
          >
            {step.options && (
              <Options
                options={step.options.map((option) => ({
                  title: `${option.optionText}`,
                  id: option.id,
                }))}
                group={step.id || 'root'}
                selectedOption={selectedContentfulSteps[i]}
                selectOption={(dest) => {
                  setSelectedContentfulSteps([
                    ...selectedContentfulSteps.slice(0, i),
                    dest,
                  ]);

                  const option = step.options.find(
                    (optionItem) => optionItem.id === dest
                  );
                  setSelectedOptionText(option ? option.text : '');

                  const nextOptionStepId =
                    option && option.step && option.step.id;
                  const nextStepTitle = allSteps.find(
                    (item) => item.id === nextOptionStepId
                  )?.title;

                  setTimeout(() => {
                    setLiveTitle(nextStepTitle || '');
                  }, 1000);
                }}
                legend={<StepTitle title={step.title} text={step.bodyText} />}
              />
            )}
          </QuestionBox>
        )
      )}

      {selectedContentfulSteps.length > 0 && (
        <StartAgain
          bg="secondary-outline"
          onClick={() => setSelectedContentfulSteps([])}
        >
          Start again
        </StartAgain>
      )}

      <VisuallyHidden aria-live="polite">{selectedOptionText}</VisuallyHidden>
      <VisuallyHidden aria-live="polite">{liveTitle}</VisuallyHidden>
    </form>
  );
};

const stepPropTypes = {
  id: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  bodyText: RichTextPropTypes,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      text: PropTypes.string.isRequired,
      step: PropTypes.shape({
        id: PropTypes.string.isRequired,
      }).isRequired,
    })
  ),
};

Stepper.propTypes = {
  ...stepPropTypes,
  allSteps: PropTypes.arrayOf(PropTypes.shape(stepPropTypes)),
};

export default Stepper;

export const StepperAssembly = ({ contentfulData }) => (
  <Stepper
    title={contentfulData.title}
    bodyText={contentfulData.bodyText}
    options={contentfulData.options}
    allSteps={contentfulData.allSteps}
  />
);
StepperAssembly.propTypes = {
  contentfulData: PropTypes.object,
};
